/**
 * Country codes and names — the one source for every country picker.
 *
 * Codes are ISO 3166-1 alpha-2. Names come from `Intl.DisplayNames` in the
 * user's language, the same way currency names do (`@/lib/currency`) — there
 * is no name table to translate. The code is the fallback on a runtime
 * without region data.
 */

/**
 * The countries the app offers as an organization / business / person
 * country. Union of the lists the pickers carried individually before this
 * module existed; the org's country also gates jurisdiction config
 * (`@/config/jurisdictionIdentity`) and the tax jurisdiction picker.
 */
export const SUPPORTED_COUNTRY_CODES = [
  "AT",
  "AU",
  "BE",
  "CA",
  "CH",
  "DE",
  "DK",
  "ES",
  "FI",
  "FR",
  "GB",
  "HK",
  "IE",
  "IT",
  "JP",
  "LU",
  "NL",
  "NO",
  "NZ",
  "PT",
  "SE",
  "SG",
  "US",
] as const;

export type SupportedCountryCode = (typeof SUPPORTED_COUNTRY_CODES)[number];

export function isSupportedCountryCode(code: unknown): code is SupportedCountryCode {
  return (
    typeof code === "string" &&
    (SUPPORTED_COUNTRY_CODES as readonly string[]).includes(code.toUpperCase())
  );
}

/** A country's name in `locale`, or the code itself when the runtime cannot name it. */
export function getCountryName(code: string, locale?: string): string {
  const upper = code.trim().toUpperCase();
  if (!upper) return "";
  try {
    return new Intl.DisplayNames([locale || "en"], { type: "region" }).of(upper) ?? upper;
  } catch {
    return upper;
  }
}

/** `{ code, name }` for each of `codes`, sorted by the localized name. */
export function countryOptions(
  locale?: string,
  codes: readonly string[] = SUPPORTED_COUNTRY_CODES
): Array<{ code: string; name: string }> {
  return codes
    .map((code) => ({ code, name: getCountryName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale || "en"));
}

/**
 * A point inside each supported country to bias address search toward.
 *
 * Azure Maps Search treats `lat`/`lon` as a soft relevance signal, not a
 * filter: it re-ranks ambiguous matches toward this point without excluding
 * anything. The value is the country's main population centre (Utrecht for
 * NL — the middle of the Randstad — rather than the geographic centroid in
 * the IJsselmeer). Coarse on purpose: a few hundred km off is still enough
 * to put "Kerkstraat 12" in the right country. Replace with an org-level
 * point once one exists.
 */
export const COUNTRY_SEARCH_BIAS: Record<SupportedCountryCode, { lat: number; lon: number }> = {
  AT: { lat: 48.2082, lon: 16.3738 },
  AU: { lat: -33.8688, lon: 151.2093 },
  BE: { lat: 50.8503, lon: 4.3517 },
  CA: { lat: 43.6532, lon: -79.3832 },
  CH: { lat: 47.3769, lon: 8.5417 },
  DE: { lat: 50.1109, lon: 8.6821 },
  DK: { lat: 55.6761, lon: 12.5683 },
  ES: { lat: 40.4168, lon: -3.7038 },
  FI: { lat: 60.1699, lon: 24.9384 },
  FR: { lat: 48.8566, lon: 2.3522 },
  GB: { lat: 51.5074, lon: -0.1278 },
  HK: { lat: 22.3193, lon: 114.1694 },
  IE: { lat: 53.3498, lon: -6.2603 },
  IT: { lat: 41.9028, lon: 12.4964 },
  JP: { lat: 35.6762, lon: 139.6503 },
  LU: { lat: 49.6116, lon: 6.1319 },
  NL: { lat: 52.0907, lon: 5.1214 },
  NO: { lat: 59.9139, lon: 10.7522 },
  NZ: { lat: -36.8485, lon: 174.7633 },
  PT: { lat: 38.7223, lon: -9.1393 },
  SE: { lat: 59.3293, lon: 18.0686 },
  SG: { lat: 1.3521, lon: 103.8198 },
  US: { lat: 39.8283, lon: -98.5795 },
};
